import { ColumnDef, Row, RowSelectionState, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Box, Flex, Text } from "@chakra-ui/react";
import { Search2Icon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useCallback, useMemo, useState, ReactElement } from "react";
import { SortIcon } from "@/components/SortIcon/SortIcon";
import { SortingTypes } from "@/helpers/SortingTypes";
import { BaseTableSkeleton } from "@/components/BaseTable/Skeleton/BaseTableSkeleton";
import "./styles.css";

interface BaseTableProps<T extends { id: string | number }> {
  list: ReadonlyArray<T>;
  columns: ColumnDef<T, any>[];
  clickHandler?: (id: string, event?: React.MouseEvent) => void;
  withoutNavigate?: boolean;
  maxHeight?: string;
  minHeight?: string;
  canSelect?: boolean;
  rowClick?: (row: Row<T>) => void;
  sortHandler?: (column: { id: string; direction: SortingTypes | null | undefined }) => void;
  sortState?: {
    column: string;
    direction: SortingTypes | null | undefined;
  };
  isLoading?: boolean;
  rowStyleFn?: (row: T) => React.CSSProperties;
}

export const BaseTable = <T extends { id: string | number }>({
  list,
  columns,
  clickHandler,
  withoutNavigate = false,
  maxHeight = "68vh",
  minHeight = "500px",
  canSelect = false,
  rowClick,
  sortHandler,
  sortState,
  isLoading = false,
  rowStyleFn,
}: BaseTableProps<T>): ReactElement => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const navigate = useNavigate();

  const table = useReactTable({
    data: list as T[],
    columns: columns as ColumnDef<T, any>[],
    enableRowSelection: true,
    enableMultiRowSelection: false,
    defaultColumn: {
      enableColumnFilter: false,
      enableSorting: false,
      size: 160,
      minSize: 40,
      maxSize: 600,
      enableResizing: false,
    },
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const columnsLength = useMemo(() => headerGroups.reduce((acc, g) => acc + g.headers.length, 0), [headerGroups]);

  const toIdString = useCallback((id: string | number): string => String(id), []);

  const handleSortClick = useCallback(
    (columnId: string) => {
      if (!sortHandler) return;
      const nextDirection =
        sortState?.direction === SortingTypes.ASC ? SortingTypes.DESC : sortState?.direction === SortingTypes.DESC ? null : SortingTypes.ASC;
      sortHandler({ id: columnId, direction: nextDirection });
    },
    [sortHandler, sortState?.direction],
  );

  const handleRowMouseDown = useCallback(
    (e: React.MouseEvent, row: Row<T>) => {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;
      if (canSelect && rowClick) {
        rowClick(row);
        return;
      }
      const idString = toIdString((row.original as T).id);
      if (clickHandler) {
        if (e.ctrlKey || e.metaKey || e.button === 1) {
          window.open(`${window.location.pathname}/${idString}`, "_blank");
        } else {
          clickHandler(idString, e);
        }
        return;
      }
      if (withoutNavigate) return;
      if (e.ctrlKey || e.metaKey || e.button === 1) {
        window.open(`${window.location.pathname}/${idString}`, "_blank");
        return;
      }
      if (e.button === 0) navigate(`${idString}`);
    },
    [canSelect, rowClick, clickHandler, withoutNavigate, navigate, toIdString],
  );

  return (
    <Flex w="100%" flexDirection="column" minH={minHeight}>
      {isLoading ? (
        <BaseTableSkeleton rows={10} columns={columnsLength} maxHeight={maxHeight} minHeight={minHeight} />
      ) : (
        <Box
          className="scroll-table-body-base"
          backgroundColor="white"
          maxH={maxHeight}
          border="1px solid #e1e7ef"
          borderRadius="8px"
          boxShadow="sm"
          overflow="auto"
          marginRight="0px"
        >
          <table
            style={{
              minWidth: "max-content",
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: "0 0",
              height: "100%",
              tableLayout: "auto",
            }}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                left: 0,
                backgroundColor: "white",
                textAlign: "left",
              }}
            >
              {headerGroups.map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        padding: "16px",
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        backgroundColor: "white",
                        position: "sticky",
                        top: 0,
                        zIndex: 100,
                        width: `${header.column.getSize()}px`,
                        minWidth: `${header.column.columnDef.minSize ?? 40}px`,
                      }}
                    >
                      <Flex alignItems="center" gap="8px">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSortClick(header.column.id);
                            }}
                          >
                            <SortIcon sort={sortState?.direction} />
                          </Box>
                        )}
                      </Flex>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody
              style={{
                borderCollapse: "separate",
                borderSpacing: "0 10px",
              }}
            >
              {rows.length > 0 ? (
                rows.map((row) => {
                  const customStyles = rowStyleFn ? rowStyleFn(row.original) : {};
                  const defaultStyles = {
                    height: "55px",
                    backgroundColor: row.getIsSelected() ? "#42AAFF" : "white",
                    color: "#3b515e",
                    borderBottom: "1px solid #e1e7ef",
                  };

                  return (
                    <tr
                      key={row.id}
                      style={{
                        ...defaultStyles,
                        ...customStyles,
                      }}
                      onMouseDown={(e) => handleRowMouseDown(e, row as Row<T>)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            textAlign: "left",
                            borderBottom: "1px solid #e1e7ef",
                            padding: "16px",
                            width: `${cell.column.getSize()}px`,
                            minWidth: `${cell.column.columnDef.minSize ?? 40}px`,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {clickHandler || withoutNavigate ? (
                            <>{flexRender(cell.column.columnDef.cell, cell.getContext())}</>
                          ) : (
                            <a
                              href=""
                              onClick={(e) => {
                                e.preventDefault();
                              }}
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                                display: "block",
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </a>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columnsLength}>
                    <Flex alignItems="center" justifyContent="center" flexDirection="column" padding="64px 0">
                      <Search2Icon w="64px" h="64px" />
                      <Text fontSize={32}>Ничего не найдено</Text>
                    </Flex>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      )}
    </Flex>
  );
};
